/**
 * Mises de côté — ce qu'il faut aller retirer du rayon pour les commandes.
 *
 * Une commande eshop déduit le stock dès sa création, mais la marchandise, elle,
 * RESTE physiquement en rayon : le 12/09/2026 le dernier Chèvre Frais Nature d'une
 * commande de 187,50 € a été encaissé au comptoir six heures avant la préparation,
 * personne au magasin ne sachant qu'il était réservé. Mesuré depuis : 21 lignes sur
 * 6 des 11 commandes de l'eshop sont tombées à zéro de cette façon. La parade est
 * humaine — aller mettre le produit de côté — et ce client ne sert qu'à la déclencher.
 *
 * Requêtes en `fetch` NU, et non par le client applicatif `services/api.ts` :
 * son intercepteur renvoie la page sur /login à tout 401 (panier compris). Un
 * sondage de fond qui tomberait sur un jeton expiré mettrait la caisse dehors en
 * pleine journée — hors de question pour une fenêtre d'information. Même patron
 * que `stock-out.api.ts`.
 *
 * ⚠️ Contrat SUPPOSÉ (endpoints écrits par le lot API, pas encore en place au
 * moment où ceci est écrit) : à revérifier avant déploiement. Le client tolère
 * les champs absents plutôt que de casser l'écran de caisse.
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

export interface ArticleAMettreDeCote {
  /** Identifiant de la LIGNE de commande : c'est elle qu'on acquitte, pas la commande. */
  line_id: string;
  product_id?: string | null;
  product_name: string;
  /**
   * Quantité COMMANDÉE (figée à la commande). Surtout pas la quantité courante de
   * la ligne : elle est écrasée par la pesée de la préparation (mesuré : un poivron
   * commandé 120 g affiché 184 g après pesée).
   */
  quantite?: number | null;
  /** 'piece' | 'kg' | 'grams' — décide de l'affichage (× 2, 1,5 kg, 300 g). */
  unite?: string | null;
  rayon?: string | null;
  /** Rayon glacière (Frais / Crémerie / Surgelés), cf. common/preparation-froid.ts. */
  froid?: boolean;
  /** Stock RAYON restant, relu à chaque tour de ronde (il bouge entre deux lectures). */
  stock_rayon?: number | null;
  /** Ce qui dort en réserve, hors de portée du comptoir. */
  stock_reserve?: number;
  /** 'kg' pour un produit au poids, sinon des pièces. */
  stock_unite?: string | null;
}

/** Une ligne que quelqu'un vient d'aller chercher — c'est par là qu'on apprend QUI. */
export interface AcquittementRecent {
  line_id: string;
  order_number?: string | null;
  product_name?: string | null;
  mis_de_cote_at: string;
  mis_de_cote_par_nom?: string | null;
}

export interface CommandeAMettreDeCote {
  order_id: string;
  order_number?: string | null;
  customer_name?: string | null;
  /** Jour de retrait (ISO). Le magasin ne donne pas d'heure : le client vient au message « prête ». */
  delivery_date?: string | null;
  /** Créneau, si un jour il en existe un. */
  pickup_slot?: string | null;
  articles: ArticleAMettreDeCote[];
}

export interface AcquittementMiseDeCote {
  /** Nombre de lignes effectivement acquittées (0 = un autre poste avait déjà tout fait). */
  acquittees?: number;
  /** Ce qu'un autre poste avait déjà fait : l'écran le dit plutôt que de laisser croire à un clic sans effet. */
  deja_acquittees?: number;
  at?: string | null;
  par_nom?: string | null;
}

export interface ReponseMisesDeCote {
  commandes: CommandeAMettreDeCote[];
  /** Acquittements des dernières minutes, tous postes confondus. */
  acquittements_recents: AcquittementRecent[];
}

function enteteRequete(): Record<string, string> {
  const entetes: Record<string, string> = { 'Content-Type': 'application/json' };

  const jeton = localStorage.getItem('auth_token');
  if (jeton) entetes['Authorization'] = `Bearer ${jeton}`;

  try {
    const brut = localStorage.getItem('tenant');
    const tenant = brut ? JSON.parse(brut) : null;
    if (tenant?.id) entetes['X-Tenant-ID'] = tenant.id;
  } catch {
    /* stockage illisible : l'appel échouera proprement, la caisse n'en dépend pas */
  }

  return entetes;
}

/**
 * Les commandes payées, pas encore préparées, qui portent des articles à retirer
 * du rayon — et les acquittements des dernières minutes.
 *
 * Le serveur ne renvoie QUE les lignes restant à faire : une ligne acquittée
 * disparaît de la liste. C'est `acquittements_recents` qui permet de dire au poste
 * qui n'a pas cliqué « Sandrine y est allée à 10 h 42 » au lieu de voir la fenêtre
 * s'évaporer toute seule.
 */
export async function listerMisesDeCote(signal?: AbortSignal): Promise<ReponseMisesDeCote> {
  const reponse = await fetch(`${API_BASE_URL}/orders/mises-de-cote`, {
    method: 'GET',
    headers: enteteRequete(),
    signal,
  });

  if (!reponse.ok) {
    throw new Error(`Mises de côté indisponibles (${reponse.status})`);
  }

  const donnees = await reponse.json();
  // Le serveur peut répondre par un tableau nu ou par { commandes: [...] } ;
  // on accepte les deux plutôt que de faire dépendre la caisse d'un enrobage.
  const brutes = Array.isArray(donnees) ? donnees : donnees?.commandes;
  const commandes = Array.isArray(brutes)
    ? brutes.map((c: any) => ({
        ...c,
        articles: Array.isArray(c?.articles) ? c.articles : Array.isArray(c?.lignes) ? c.lignes : [],
      }))
    : [];

  return {
    commandes,
    acquittements_recents: Array.isArray(donnees?.acquittements_recents)
      ? donnees.acquittements_recents
      : [],
  };
}

/**
 * « C'est mis de côté » — pour TOUS les postes.
 *
 * L'acquittement porte sur les LIGNES affichées, jamais sur la commande : le
 * périmètre est recalculé sur le stock du moment, donc un article qui passe
 * sous le seuil APRÈS coup doit pouvoir rouvrir la fenêtre, seul. Acquitter la
 * commande entière rejouerait exactement l'incident du chèvre.
 */
export async function acquitterMisesDeCote(
  lineIds: string[],
): Promise<AcquittementMiseDeCote> {
  const reponse = await fetch(`${API_BASE_URL}/orders/mises-de-cote/acquitter`, {
    method: 'POST',
    headers: enteteRequete(),
    body: JSON.stringify({ line_ids: lineIds }),
  });

  if (!reponse.ok) {
    throw new Error(`Acquittement refusé (${reponse.status})`);
  }

  return (await reponse.json()) || {};
}

/**
 * « Me le rappeler demain » : les lignes affichées se taisent jusqu'à demain,
 * sur les trois caisses. Le serveur garde celles d'un retrait du jour.
 */
export async function reporterMisesDeCote(
  lineIds: string[],
): Promise<{ reportees: number; gardees: number; rappel_le: string | null }> {
  const reponse = await fetch(`${API_BASE_URL}/orders/mises-de-cote/reporter`, {
    method: 'POST',
    headers: enteteRequete(),
    body: JSON.stringify({ line_ids: lineIds }),
  });
  if (!reponse.ok) throw new Error(`Report refusé (${reponse.status})`);
  return (await reponse.json()) || { reportees: 0, gardees: 0, rappel_le: null };
}
